'use client'

import React from 'react'
import {
  BarChart3,
  Network,
  Eye,
  MessageSquare,
  Clock,
  Captions,
} from 'lucide-react'

/**
 * YouTube Brand Monitor Diagrams
 * Visual support for "YouTube Brand Monitor: What AI Hears About Your Brand".
 * Theming per CLAUDE.md: data-theme='dark', no dark: variants.
 * Brand: #008dcc #FDC304 #F90B8A #8b5cf6.
 */

interface Dim {
  label: string
  desc: string
  color: string
  icon: React.ReactNode
  hero?: boolean
}

const DIMS: Dim[] = [
  {
    label: 'Volume',
    desc: 'How many videos mention your brand',
    color: '#008dcc',
    icon: <BarChart3 size={18} aria-hidden />,
  },
  {
    label: 'Channel diversity',
    desc: 'How many distinct channels cover you',
    color: '#8b5cf6',
    icon: <Network size={18} aria-hidden />,
  },
  {
    label: 'Reach',
    desc: 'View-weighted exposure of the coverage',
    color: '#FDC304',
    icon: <Eye size={18} aria-hidden />,
  },
  {
    label: 'Sentiment',
    desc: 'Transcript-grounded on the top five videos, not the title',
    color: '#F90B8A',
    icon: <MessageSquare size={18} aria-hidden />,
    hero: true,
  },
  {
    label: 'Recency',
    desc: 'How fresh the coverage is',
    color: '#22c55e',
    icon: <Clock size={18} aria-hidden />,
  },
]

export function YouTubeDimensionsDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-10 rounded-2xl border border-border/60 bg-muted/50 p-6 md:p-8'
    >
      <div className='mb-6 flex items-start gap-3'>
        <div
          className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg'
          style={{ backgroundColor: '#F90B8A15', color: '#F90B8A' }}
        >
          <Captions size={18} aria-hidden />
        </div>
        <div>
          <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
            Five dimensions, one grade
          </p>
          <p className='mt-1 text-sm leading-relaxed text-foreground/80'>
            What the YouTube Brand Monitor scores. Sentiment is the
            differentiator: it reads the actual transcript, not the title.
          </p>
        </div>
      </div>

      <div className='grid gap-3 sm:grid-cols-2'>
        {DIMS.map(d => (
          <div
            key={d.label}
            className='rounded-xl border border-border/60 bg-background p-5'
            style={{ borderLeftWidth: '4px', borderLeftColor: d.color }}
          >
            <div className='flex items-center gap-2 text-foreground'>
              <span style={{ color: d.color }}>{d.icon}</span>
              <span className='text-sm font-semibold leading-none'>
                {d.label}
              </span>
              {d.hero && (
                <span
                  className='ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider'
                  style={{ backgroundColor: `${d.color}15`, color: d.color }}
                >
                  transcript-grounded
                </span>
              )}
            </div>
            <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
              {d.desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
